import type { ExpectedPayer, VerifiedSlip } from '@pf/contracts';
import { ArrowRight, Check, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { bankInfo, isSameBank } from '@/lib/banks';
import { cn } from '@/lib/utils';
import { CopyButton } from './copy-button';
import { normalizeName } from './payee-diff';

/**
 * Registered payer versus the slip's disclosed sender — verify-the-sender, ต้นทาง.
 *
 * {@link PayeeDiff} answers "did the money land on the right account" (ปลายทาง); this answers the
 * other half of the same question — "did it come from the account the customer registered"
 * (ต้นทาง). Both are shown on the manual-review screen because a slip can attest a perfect
 * receiver while coming from an account the depositor never registered, and SAD §6's
 * verify-the-sender extension exists precisely to catch that.
 *
 * Two things are optional here in a way {@link PayeeDiff} never has to consider, and both must
 * read as an explicit fact rather than a blank cell (a blank reads as "we don't know", which is
 * not what either absence means):
 *
 *  - `expected` is `null` when the reservation carried no `payer_account` at all — a reservation
 *    that predates verify-the-sender, or a participant that has not adopted it. The check never
 *    applied; there is nothing to compare.
 *  - the slip's `sender` is `undefined` when the provider verified the slip but disclosed nothing
 *    about who sent it — real providers routinely omit this.
 */

/** Props for {@link PayerDiff}. */
export interface PayerDiffProps {
  /** The reservation's declared payer account, or `null` when none was ever supplied. */
  expected: ExpectedPayer | null;
  /** The verified slip, or `null` when none was ever verified for this transaction. */
  actual: VerifiedSlip | null;
  /**
   * Fuzzy sender-name score from the matcher, when known — the ต้นทาง counterpart of
   * {@link PayeeDiffProps.nameScore}.
   *
   * Server-computed, never derived here: the `matches` tick below is exact equality after a
   * *simplified* local normalisation, which is a cheap approximation of SAD §6's matcher and
   * would put a different number on screen from the one the sender check actually decided on.
   */
  nameScore?: number | null | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

interface Row {
  label: string;
  expected: string;
  actual: string | null;
  matches: boolean | null;
  note?: string | undefined;
  /** What a copy button beside the *expected* value puts on the clipboard; none when omitted. */
  copyable?: boolean | undefined;
}

/** Render the expected-vs-actual payer (sender) comparison. */
export function PayerDiff({ expected, actual, nameScore, className }: PayerDiffProps): ReactNode {
  const { t } = useTranslation();
  const sender = actual?.sender;

  const body = ((): ReactNode => {
    // Only a missing *expected* side leaves nothing to render: without a registered payer
    // account the left column would be blank rows, so the absence is stated in words instead.
    if (expected === null) {
      const messageKey =
        actual === null
          ? 'domain.payerDiff.noSlipMessage'
          : sender === undefined
            ? 'domain.payerDiff.neitherMessage'
            : 'domain.payerDiff.noPayerAccountMessage';
      return <p className="px-4 py-6 text-sm text-muted-foreground">{t(messageKey)}</p>;
    }

    // A missing slip or undisclosed sender is different: the registered account is exactly what
    // the operator needs on screen to eyeball against the customer's uploaded slip image, so the
    // rows render with the expected side filled and the actual column degrades to a placeholder.
    const senderName = sender?.name;
    const senderBank = sender?.bank;
    const senderLast4 = sender?.account_last4;
    const expectedBank = bankInfo(expected.bank);
    const rows: Row[] = [
      {
        label: t('domain.payerDiff.accountName'),
        expected: expected.name,
        actual: senderName ?? null,
        matches:
          senderName === undefined
            ? null
            : normalizeName(expected.name) === normalizeName(senderName),
        // The same line PayeeDiff carries, for the same reason: the matcher scores the
        // *normalised* pair, so showing only the raw strings makes a perfectly good 0.94 look
        // like a mismatch to a human.
        note:
          senderName === undefined
            ? undefined
            : nameScore !== null && nameScore !== undefined
              ? t('domain.payerDiff.nameNoteWithScore', {
                  expectedName: normalizeName(expected.name),
                  actualName: normalizeName(senderName),
                  score: nameScore.toFixed(2),
                })
              : t('domain.payerDiff.nameNote', {
                  expectedName: normalizeName(expected.name),
                  actualName: normalizeName(senderName),
                }),
        copyable: true,
      },
      {
        label: t('domain.payerDiff.bank'),
        expected: `${expectedBank.name} (${expected.bank})`,
        actual: senderBank === undefined ? null : `${bankInfo(senderBank).name} (${senderBank})`,
        matches: senderBank === undefined ? null : isSameBank(expected.bank, senderBank),
      },
      {
        label: t('domain.payerDiff.accountNumber'),
        // The full number, not `last4`: it is what an operator pastes into a bank portal, and the
        // account row is the one place on this screen it appears. The *comparison* stays on
        // last-4, because that is all a provider ever discloses about the sender.
        expected: expected.number,
        actual: senderLast4 ?? null,
        matches: senderLast4 === undefined ? null : senderLast4 === expected.last4,
        note: senderLast4 === undefined ? undefined : t('domain.payerDiff.accountNumberNote'),
        copyable: true,
      },
    ];

    const footerKey =
      actual === null
        ? 'domain.payerDiff.noSlipCompareHint'
        : sender === undefined
          ? 'domain.payerDiff.noSenderDisclosedMessage'
          : null;

    return (
      <>
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <li
              key={row.label}
              className="grid grid-cols-1 gap-x-3 gap-y-1 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center"
              data-field={row.label}
              data-matches={row.matches === null ? 'unknown' : String(row.matches)}
            >
              <div className="min-w-0">
                <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                  {row.label}
                </p>
                <p className="flex items-center gap-1 break-words text-sm text-foreground">
                  <span className="min-w-0 break-words">{row.expected}</span>
                  {row.copyable === true ? (
                    <CopyButton
                      value={row.expected}
                      label={row.label}
                      className="size-5 shrink-0 [&_svg]:size-3"
                    />
                  ) : null}
                </p>
              </div>

              <div className="hidden items-center justify-center sm:flex">
                {row.matches === true ? (
                  <span
                    className="flex size-6 items-center justify-center rounded-full bg-state-good-bg text-state-good"
                    title={t('domain.payerDiff.matchesTitle')}
                  >
                    <Check className="size-3.5" aria-hidden="true" />
                    <span className="sr-only">{t('domain.payerDiff.matchesSr')}</span>
                  </span>
                ) : row.matches === false ? (
                  <span
                    className="flex size-6 items-center justify-center rounded-full bg-state-bad-bg text-state-bad"
                    title={t('domain.payerDiff.doesNotMatchTitle')}
                  >
                    <X className="size-3.5" aria-hidden="true" />
                    <span className="sr-only">{t('domain.payerDiff.doesNotMatchSr')}</span>
                  </span>
                ) : (
                  <ArrowRight className="size-4 text-muted-foreground" aria-hidden="true" />
                )}
              </div>

              <div className="min-w-0">
                <p className="sr-only">
                  {t('domain.payerDiff.fieldOnSlipSr', { label: row.label })}
                </p>
                <p
                  className={cn(
                    'flex items-center gap-1 break-words text-sm',
                    row.matches === false
                      ? 'font-semibold text-state-bad'
                      : row.actual === null
                        ? 'text-muted-foreground'
                        : 'text-foreground',
                  )}
                >
                  <span className="min-w-0 break-words">
                    {row.actual ??
                      t(
                        actual === null
                          ? 'domain.payerDiff.noSlipCell'
                          : 'domain.payerDiff.notProvided',
                      )}
                  </span>
                  {/* Only over a real attested value — a placeholder ("not disclosed") is not
                      something anyone wants on their clipboard. */}
                  {row.copyable === true && row.actual !== null ? (
                    <CopyButton
                      value={row.actual}
                      label={t('domain.payerDiff.fieldOnSlipSr', { label: row.label })}
                      className="size-5 shrink-0 [&_svg]:size-3"
                    />
                  ) : null}
                </p>
                {row.note !== undefined ? (
                  <p className="pt-1 text-2xs leading-relaxed text-muted-foreground">{row.note}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        {footerKey ? (
          <p className="border-t border-border bg-muted/30 px-4 py-3 text-2xs leading-relaxed text-muted-foreground">
            {t(footerKey)}
          </p>
        ) : null}
      </>
    );
  })();

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border', className)}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 border-b border-border bg-muted/50 px-4 py-2 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>{t('domain.payerDiff.expectedPayerHeader')}</span>
        <span className="sr-only">{t('domain.payerDiff.comparedWith')}</span>
        <span aria-hidden="true" />
        <span>{t('domain.payerDiff.attestedPayerHeader')}</span>
      </div>
      {body}
    </div>
  );
}
